<template>
	<header
		v-if="!headless"
		class="breadcrumb"
	>
		<span>Storico Telemetrie</span>

		<div class="breadcrumb-actions">
			<ViewToggle
				id="toggle-button"
				class="toggle-icon"
			/>

			<NotificationBell
				v-if="showCrumbBell"
				placement="header"
				:deviceId="props.deviceId"
			/>
		</div>
	</header>

	<div class="charts-page">
		<!-- Environment Section -->
		<div
			class="dash-section"
			v-if="preferences.environmentSection"
		>
			<div class="section-header-main">
				<h2 class="telemetric-subtitle">ENVIRONMENT AND SENSORS CURRENT VALUES</h2>
			</div>

			<EnvironmentSection
				:device-id="props.deviceId"
				:device-name="props.deviceName"
				:location="props.currentLocation"
				:current-temp-c="props.currentTempC || 0"
				:outside-hum="props.outsideHum || 0"
				:last-update-ago="props.lastUpdateAgo || 'N/D'"
				:chart-range="props.chartRangeMet || '24h'"
				:series-t="props.seriesT || []"
				:series-h="props.seriesH || []"
				:temp-min="props.tempMin"
				:temp-max="props.tempMax"
				:hum-min="props.humMin"
				:hum-max="props.humMax"
				:show-map="true"
				@refresh="emit('refresh')"
				@center="emit('center')"
				@copy-coords="emit('copy-coords')"
				@update:range="emit('update:range-met', $event)"
			/>
		</div>



		<!-- Toolbar grafici (sempre visibile: desktop + mobile) -->
		<div class="charts-toolbar">
			<div class="charts-toolbar-left">
				<span class="toolbar-title">DYNAMIC GRAPHS</span>

				<label
					class="toolbar-field"
					:class="{ 'is-disabled': isMobile }"
				>
					<span class="toolbar-field-label">Graphs/row</span>

					<select
						class="toolbar-select"
						v-model.number="columns"
						:disabled="isMobile"
					>
						<option :value="1">1</option>
						<option :value="2">2</option>
						<!-- <option :value="4">4</option> -->
					</select>
				</label>

				<div
					v-if="isMobile"
					class="charts-toolbar-hint"
				>
					(Il layout è fisso a 1 colonna su dispositivi mobili)
				</div>
			</div>

			<div class="charts-toolbar-right">				
				<button
					class="btn-toolbar"
					type="button"
					@click="showChartConfig = true"
					title="Scegli quali grafici mostrare"
				>
					<FontAwesomeIcon :icon="faSliders" />
					<span>Customize</span>
				</button>

				<button
					class="btn-toolbar"
					type="button"
					@click="showQuickView = true"
					title="Visualizzazione Rapida"
				>
					<FontAwesomeIcon :icon="faEye" />
					<span>Quick View</span>
				</button>
			</div>
		</div>

		<div
			v-if="!isMobile"
			class="charts-toolbar-hint"
		>
			Scegli il layout, quali grafici visualizzare ed eventualmente quali mettere temporaneamente in primo piano.
		</div>
		<div
			v-else
			class="charts-toolbar-hint"
		>
			Configura i grafici da visualizzare ed eventualmente quali mettere temporaneamente in primo piano.
		</div>

		<!-- Dynamic Charts Grid -->
		<div
			class="charts-grid"
			:style="gridStyle"
		>
			<div
				v-for="chartId in visibleCharts"
				:key="chartId"
				class="dash-section"
			>
				<!-- Combined Chart -->
				<template v-if="getCrossData(chartId)">
					<CrossChart
						:deviceId="props.deviceId"
						:entities="getCrossData(chartId).entities"
						:title="getCrossData(chartId).title"
						:initialRange="sharedRange"
						:initialSelectedDay="sharedSelectedDay"
						@update:range="updateRange"
						@update:selectedDay="updateSelectedDay"
					/>
				</template>

				<!-- Single Sensor Chart -->
				<template v-else>
					<StateChart
						v-if="STATE_SENSORS.includes(chartId)"
						:entity="chartId"
						:deviceId="props.deviceId"
						:initialRange="sharedRange"
						:initialSelectedDay="sharedSelectedDay"
						@update:range="updateRange"
						@update:selectedDay="updateSelectedDay"
					/>
					<ChartBox
						v-else
						:entity="chartId"
						:deviceId="props.deviceId"
						:initialRange="sharedRange"
						:initialSelectedDay="sharedSelectedDay"
						@update:range="updateRange"
						@update:selectedDay="updateSelectedDay"
					/>
				</template>
			</div>
		</div>

		<!-- Chart Visibility Config Modal (sempre accessibile) -->
		<ChartVisibilityModal
			v-if="showChartConfig"
			device-type="static"
			@close="showChartConfig = false"
		/>
		
		<!-- Quick View Modal -->
		<QuickChartPickerModal
			v-if="showQuickView"
			@close="showQuickView = false"
		/>
	</div>
</template>

<script setup>
	import ChartBox from "@/components/data/ChartBox.vue";
	import ChartVisibilityModal from "@/components/data/ChartVisibilityModal.vue";
	import QuickChartPickerModal from "@/components/data/QuickChartPickerModal.vue";
	import CrossChart from "@/components/data/CrossChart.vue";
	import EnvironmentSection from "@/components/data/EnvironmentSection.vue";
	import StateChart from "@/components/data/StateChart.vue";
	import NotificationBell from "@/components/NotificationBell.vue";
	import ViewToggle from "@/components/ViewToggle.vue";

	import { useChartVisibility } from "@/composables/useChartVisibility";
	import { CROSS_LIST, STATE_SENSORS } from "@/config/chartConfig";

	import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from "vue";

	import { faSliders, faEye } from "@fortawesome/free-solid-svg-icons";
	import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

	/* injections */
	const isDesktop = inject("isDesktop");
	const isSidebarOpen = inject("isSidebarOpen");

	/* props / emits */
	const emit = defineEmits(["refresh", "center", "copy-coords", "update:range-met"]);

	const props = defineProps({
		deviceId: [String, Number],
		headless: Boolean,

		// Enviroment Props
		deviceName: String,
		currentLocation: Array,
		currentTempC: Number,
		outsideHum: Number,
		lastUpdateAgo: String,
		chartRangeMet: String,
		seriesT: Array,
		seriesH: Array,
		tempMin: Number,
		tempMax: Number,
		humMin: Number,
		humMax: Number,
	});

	/* bell visibility */
	const showCrumbBell = computed(() => !isDesktop?.value || (isDesktop?.value && !isSidebarOpen?.value));

	/* layout columns */
	const columns = ref(2);
	const viewportW = ref(window.innerWidth);
	const isMobile = computed(() => viewportW.value < 860);

	function onResize() {
		viewportW.value = window.innerWidth;
	}

	onMounted(() => {
		window.addEventListener("resize", onResize);

		const saved = localStorage.getItem("telemetry.columns");
		if (saved) columns.value = Number(saved) || 2;
	});

	onBeforeUnmount(() => {
		window.removeEventListener("resize", onResize);
	});

	watch(columns, (v) => {
		localStorage.setItem("telemetry.columns", String(v));
	});

	const gridStyle = computed(() => {
		const cols = isMobile.value ? 1 : columns.value;
		return { "--cols": String(cols) };
	});

	/* toolbar modals */
	const showChartConfig = ref(false);
	const showQuickView = ref(false);

	/* shared chart state */
	const sharedRange = ref("24h");
	const sharedSelectedDay = ref(null);

	/* chart visibility prefs */
	const { preferences, temporaryChart } = useChartVisibility();

	/* Compute visible charts including temporary one */
	const visibleCharts = computed(() => {
		const list = [...preferences.value.orderedCharts];
		if (temporaryChart.value) {
			// Remove if already present to avoid duplicates, then unshift
			const idx = list.indexOf(temporaryChart.value);
			if (idx !== -1) {
				list.splice(idx, 1);
			}
			list.unshift(temporaryChart.value);
		}
		return list;
	});

	function updateRange(newRange) {
		sharedRange.value = newRange;
	}

	function updateSelectedDay(newDay) {
		sharedSelectedDay.value = newDay;
	}

	function getCrossData(id) {
		return CROSS_LIST.find((c) => c.id === id);
	}
</script>

<style scoped>
	.breadcrumb {
		position: sticky;
		top: 0;
		z-index: 10;
		height: 56px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 0 24px;
		background: #0d0d0d;
		font: 600 18px/56px "Inter", sans-serif;
		border-bottom: 2px solid #1d3a3a;
		border-image: linear-gradient(to bottom, #535454, #2d3e4f) 1;
	}

	.breadcrumb-actions {
		display: flex;
		align-items: center;
		gap: 12px;
	}

	.toggle-icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 6px;
		border-radius: 8px;
		border: 1px solid transparent;
		background: transparent;
		color: #e6e6e6;
		cursor: pointer;
		transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, transform 0.06s ease;
		gap: 8px;
	}

	.toggle-icon:hover {
		background: #14181d;
		border-color: #2a2f34;
		box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.04);
		transform: translateY(-1px);
	}

	.charts-page {
		padding: 20px;
		height: auto;
	}

	/* ---------- Toolbar grafici ---------- */
	.charts-toolbar {
		display: flex;
		align-items: center;
		justify-content: space-between;

		margin: 12px 20px 0;
		padding: 10px 12px;

		border-radius: 12px;
		border: 1px solid rgba(255, 255, 255, 0.06);
		background: linear-gradient(180deg, rgba(20, 24, 29, 0.78), rgba(10, 12, 14, 0.78));
		box-shadow: 0 10px 22px rgba(0, 0, 0, 0.28);

		backdrop-filter: blur(6px);
		-webkit-backdrop-filter: blur(6px);

		gap: 12px;
	}

	.charts-toolbar-left {
		display: flex;
		align-items: center;
		gap: 14px;
		flex-wrap: wrap;
	}

	.toolbar-title,
	.telemetric-subtitle {
		font: 800 12px/1 "Inter", system-ui, sans-serif;
		color: rgba(159, 176, 194, 0.92);
		letter-spacing: 0.12em;
		text-transform: uppercase;
		white-space: nowrap;
		padding-top: 6px;
	}

	.toolbar-field {
		display: inline-flex;
		align-items: center;
		gap: 10px;
		padding: 6px 10px;
		border-radius: 10px;
		background: rgba(255, 255, 255, 0.03);
		border: 1px solid rgba(255, 255, 255, 0.06);
	}

	.toolbar-field.is-disabled {
		opacity: 0.75;
	}

	.toolbar-field-label {
		font: 650 13px/1 "Inter", system-ui, sans-serif;
		color: rgba(230, 230, 230, 0.85);
		white-space: nowrap;
	}

	.toolbar-select {
		appearance: none;
		-webkit-appearance: none;
		-moz-appearance: none;

		background: rgba(20, 24, 29, 0.92);
		color: #e6e6e6;

		border: 1px solid rgba(255, 255, 255, 0.1);
		border-radius: 10px;

		padding: 7px 34px 7px 12px;
		font: 800 13px/1 "Inter", system-ui, sans-serif;

		cursor: pointer;
		outline: none;

		background-image: linear-gradient(45deg, transparent 50%, rgba(230, 230, 230, 0.85) 50%), linear-gradient(135deg, rgba(230, 230, 230, 0.85) 50%, transparent 50%);
		background-position: calc(100% - 18px) 50%, calc(100% - 12px) 50%;
		background-size: 6px 6px, 6px 6px;
		background-repeat: no-repeat;
	}

	.toolbar-select:disabled {
		cursor: not-allowed;
		opacity: 0.7;
	}

	.toolbar-select:focus-visible {
		border-color: rgba(0, 143, 143, 0.55);
		box-shadow: 0 0 0 3px rgba(0, 143, 143, 0.18);
	}

	.charts-toolbar-right {
		display: flex;
		align-items: center;
		gap: 10px;
		flex: 0 0 auto;
	}

	.btn-toolbar {
		display: inline-flex;
		align-items: center;
		gap: 8px;

		padding: 8px 10px;
		border-radius: 10px;
		border: 1px solid rgba(0, 143, 143, 0.35);
		background: rgba(0, 143, 143, 0.12);

		color: #e6e6e6;
		font: 800 13px/1 "Inter", system-ui, sans-serif;
		cursor: pointer;

		transition: transform 0.06s ease, background 0.15s ease, border-color 0.15s ease;
		white-space: nowrap;
	}

	.btn-toolbar:hover {
		background: rgba(0, 143, 143, 0.18);
		border-color: rgba(0, 143, 143, 0.55);
		transform: translateY(-1px);
	}

	.btn-toolbar:active {
		transform: translateY(0);
	}

	.charts-toolbar-hint {
		margin: 6px 20px 6px;
		font: 500 12px/1.2 "Inter", system-ui, sans-serif;
		color: rgba(159, 176, 194, 0.65);
	}

	/* Mobile: stack verticale, ma la toolbar resta visibile */
	@media (max-width: 860px) {
		.charts-toolbar {
			margin: 10px 12px 0;
			flex-direction: column;
			align-items: stretch;
		}

		.charts-toolbar-left {
			width: 100%;
			justify-content: space-between;
		}

		.toolbar-field {
			width: 100%;
			justify-content: space-between;
		}

		.charts-toolbar-right {
			width: 100%;
		}

		.btn-toolbar {
			width: 100%;
			justify-content: center;
		}

		.charts-toolbar-hint {
			margin: 6px 12px 6px;
		}
	}

	/* ---------- Grid grafici ---------- */
	.charts-grid {
		width: 100%;
		max-width: 100%;
		display: grid;
		grid-template-columns: repeat(var(--cols), minmax(0, 1fr));
		gap: 16px;
	}
</style>
